import { requestStu } from '@/api';
import { clientApiAxiosInstance as client, publicApi } from '@/request/index';

import { getTimestamp } from './nav';

export interface ContentData<T> {
  content: T;
}

export interface Country {
  areaCode: string;
  name: string;
  value: string;
}

export interface CountriesRes {
  local: Country;
  countries: Country[];
}

export const GetCountryList = () => {
  return client.get<ContentData<CountriesRes>, ContentData<CountriesRes>>(
    `/public/open/countries/zh/ddl/local`,
  );
};

export interface User {
  accountData: {
    alreadySetPassword: boolean;
    associatedWeChat: boolean;
    registerBind: boolean;
  };
  token: string;
  refreshToken: string;
  uuid: string;
  email: string | null;
  errorCode?: number;
  phone: string | null;
  fullName?: string;
  oauthType?: string;
  openId?: string;
  unionId?: string;
  leadRepeat?: boolean;
}

export interface RefreshTokenRes {
  content: User;
}

export const GetTokenByRefreshToken = () => {
  return client.post<RefreshTokenRes, RefreshTokenRes>(
    '/api/authorized/refresh_token',
  );
};

export type TimezoneRes = {
  label: string;
  value: string;
}[];

export const GetTimezoneList = () => {
  return client.get<ContentData<TimezoneRes>, ContentData<TimezoneRes>>(
    `/public/open/timezones/ddl`,
  );
};

export interface RetentionInfoRes {
  age?: number;
  name?: string;
  englishName?: string;
  language?: 'A' | 'B';
}

export type TimeItem = {
  startTime: string;
  endTime: string;
  timeLabel: string;
  courseScheduleId: string | null;
  sectionRespDTO: {
    levelDisplay: string;
    levelDisplayEn: string;
    course: {
      uuid: string;
      courseName: string;
    };
  };
  classType: 'PRIVATE' | 'GROUP';
  state: number;
  dayOfWeek: number;
  duration: number;
  formClassDate: unknown;
  formClassTime: unknown;
  formClassDayOfWeek: number;
  capacity: number;
  stuFormClassesSize: number;
  courseSchedules: unknown;
  teachers: unknown;
  trail: boolean;
};

export interface GradeListRes {
  subject: 'CHINESE' | 'MATH' | 'ENGLISH';
  country: string;
  callingCode: string;
  grade: number;
  gradeEn: string; // 年级英文名
  gradeZh: string; // 年级中文名
  year: number;
}

export type Subject = 'MATH' | 'CHINESE' | 'ENGLISH';

export const GetGradeList = (subject: string) => {
  return client.post<
    ContentData<Array<GradeListRes>>,
    ContentData<Array<GradeListRes>>
  >(`/public/open/grade/list`, {
    subject,
  });
};

export const GetIpInfo = () => {
  return client.get<
    ContentData<{ timezone: string; country: string }>,
    ContentData<{
      timezone: string;
      country: string;
      callingCode: string;
    }>
  >(`/public/open/ip_info_v2`);
};

export interface ReserveReqParams {
  age: number;
  country: string;
  courseDate: string;
  courseTime: string;
  firstName: string;
  chineseLevel?: string;
  subject: string;
  timeZoneStandard: string;
  email?: string;
  phone?: string;
  englishName: string;
  communicationLanguage: string; //* eg. A B C
  grade?: number;
}

export interface ReserveRes {
  success: string;
  content: {
    until: number;
    errorCode: number;
    courseSchedule: {
      uuid: string;
    };
    student: any;
  };
}
const appEnvMap = {
  app_ios: 'IOS',
  app_android: 'ANDROID',
};

export const PostReserve = (
  data: ReserveReqParams,
  appEnv: string,
  others: any,
) => {
  return client.post<ReserveRes, ReserveRes>(`/api/reserve`, data, {
    skipErrorHandler: true,
    headers: {
      common: {
        platform: appEnvMap?.[appEnv as 'app_ios' | 'app_android'],
      },
      ...others,
    },
  });
};

export interface AvailableDateReq {
  country?: string;
  grade?: string;
  subject: 'Math' | 'Chinese' | 'English';
  timezone: string;
  operator?: string;
  chineseLevel?: string;
  language?: string;
  age: number;
}

export interface AvailableTimeReq extends AvailableDateReq {
  date: string;
}

export interface AvailableTimeRes {
  active: boolean;
  date: string;
  day: string;
  dayOfWeek: number;
  month: string;
  timeList: TimeItem[];
}

export const GetAvailableTime = (data: AvailableTimeReq) => {
  return client.post<AvailableTimeRes[], AvailableTimeRes[]>(
    `/public/self/schedule/available_time`,
    data,
  );
};

export const GetRetentionInfo = () => {
  return client.get<
    ContentData<RetentionInfoRes>,
    ContentData<RetentionInfoRes>
  >('/api/acc/track/phone');
};

export const GetShowEmailIpt = () => {
  return client.get<ContentData<boolean>, ContentData<boolean>>(
    '/api/reserve/is_input_email_v2',
  );
};

export const GetShowPhoneIpt = () => {
  return client.get<ContentData<boolean>, ContentData<boolean>>(
    '/api/reserve/is_input_mobile',
  );
};

export const GetAvailableDate = (data: AvailableDateReq) => {
  return client.post<string[], string[]>(
    `/public/self/schedule/available_date/v2`,
    data,
  );
};

export interface GetRecommendedCourseInfoReq
  extends Omit<AvailableDateReq, 'language'> {}

export const GetRecommendedCourseInfo = (data: GetRecommendedCourseInfoReq) => {
  return client.post<{ content: string }, { content: string }>(
    '/public/self/schedule/course/info/v2',
    data,
  );
};

export type SubjectUpper = 'CHINESE' | 'MATH' | 'ENGLISH';

export interface CheckPhoneNumReq {
  mobile: string;
  subject: SubjectUpper;
}

export interface CheckPhoneNumRes {
  a: '00' | '01';
  b: '00' | '01';
  c: '00' | '01';
  f: '00' | '01';
  g: string;
}

export const CheckPhoneNum = (data: CheckPhoneNumReq) => {
  return client.post<CheckPhoneNumRes, CheckPhoneNumRes>(
    '/public/self/schedule/check',
    data,
  );
};

export interface CheckEmailApiReq {
  email: string;
  mobile: string;
}

export interface CheckEmailApiRes {
  a: '01' | '00'; // 00 不可用 01 反之
}

export const CheckEmailApi = (data: CheckEmailApiReq) => {
  return client.post<CheckEmailApiRes, CheckEmailApiRes>(
    '/public/self/schedule/email/check',
    data,
  );
};

export interface CreateLeadApiReq {
  age: number;
  country: string;
  countryCode: string;
  leadType: 'Chinese' | 'Math' | 'English';
  mobile: string;
  questionData?: {
    mathShortBoard?: string[];
    englishShortBoard?: string[];
    chineseLevel?: string;
  };
  serveLanguage: string;
}

export interface OwnerInfo {
  avatar: string;
  email: string;
  name: string;
  ownerQwShortCode: string;
  qwAvatar: string | null;
  qwName: string | null;
  quickJumpOwnerUrl?: string;
}

export interface AdvisorInfo {
  qwImageUrl: string;
  crmccEmail: string;
  subject: string;
  ownerInfo?: OwnerInfo;
  defaultInfo?: OwnerInfo;
  showWA?: boolean;
}

export interface CreateLeadApiRes {}

export const CreateLeadApi = (data: CreateLeadApiReq) => {
  return client.post<CreateLeadApiRes, CreateLeadApiRes>(
    '/public/self/schedule/create_lead',
    data,
  );
};

// public/self/schedule/check/{code}/nick

export interface CheckIsNeedIptRes {
  a: '00' | '01';
  b: '00' | '01';
}

export const CheckIsNeedIpt = (code: string) => {
  return client.post<CheckIsNeedIptRes, CheckIsNeedIptRes>(
    `/public/self/schedule/check/${code}/nick`,
  );
};

// 根据 l 参数获取后台配置
export const getJsonNode = (id: string) => {
  return publicApi.get(`/public/ref/json_node/${id}`);
};

// 获取学习规划师信息
export const getAcademicAdvisorInfo = (data?: {
  scanType: string;
  studentCode?: string | null;
  subject?: string | null;
}) => {
  return client
    .post<{ content: AdvisorInfo }, { content: AdvisorInfo }>(
      'api/class/intro/show/planner_info',
      {
        ...data,
        studentCode: data?.studentCode || null,
        subject: data?.subject || null,
      },
    )
    .then(res => res?.content);
};

// 获取学习规划师资源, 无token情况
export const getAcademicAdvisorInfoNoToken = (data?: {
  scanType: string;
  studentCode?: string | null;
  subject?: string | null;
}) => {
  return publicApi
    .post<{ content: AdvisorInfo }, { content: AdvisorInfo }>(
      'public/show/planner_info',
      {
        ...data,
        studentCode: data?.studentCode || null,
        subject: data?.subject || null,
      },
    )
    .then(res => res?.content);
};
/**
 * 已登录、没有课程映射、绑定了手机号
 *
 * @param data
 * @param headers
 * @returns
 */
export const scheduleCreateLead = (data: any, headers: any) => {
  return getTimestamp().then(res => {
    return requestStu.post('/public/self/schedule/create_lead', data, {
      signature: true,
      timestamp: res,
      headers,
    });
  });
};

// 扩科
export interface RecordExpandParams {
  expandType: 'TRIAL' | 'COMPETITION';
  subject: 'CHINESE' | 'MATH' | 'ENGLISH';
  courseId?: string;
  courseName?: string;
}

export const PostRecordExpand = (params: RecordExpandParams) => {
  return client.post('/api/reserve/expand/record', params);
};
